import { computed } from '@ember/object';
import { and, equal, not, or, reads } from '@ember/object/computed';
import { equalToProperty, hash } from '@intercom/pulse/lib/computed-properties';
import Em from 'ember';
export default Em.Object.extend({
  startBlockIndex: 0,
  startItemIndex: 0,
  startPositionIndex: 0,
  endBlockIndex: 0,
  endItemIndex: 0,
  endPositionIndex: 0,
  firstSelectedBlock: reads('selectedBlocks.firstObject'),
  lastSelectedBlock: reads('selectedBlocks.lastObject'),
  hasWidth: not('isZeroWidth'),
  isMultiBlock: not('isSingleBlock'),
  isAtPositionZero: equal('startPositionIndex', 0),
  isNotAtPositionZero: not('isAtPositionZero'),
  isAtItemZero: equal('startItemIndex', 0),
  isNotAtItemZero: not('isAtItemZero'),
  isNotAtItemOrPositionZero: or('isNotAtItemZero', 'isNotAtPositionZero'),
  isAtBlockZero: equal('startBlockIndex', 0),
  isAtZero: and('isZeroWidth', 'isAtItemZero', 'isAtBlockZero', 'isAtPositionZero'),
  isNotAtZero: not('isAtZero'),
  isAtStartOfBlock: and('isAtPositionZero', 'isAtItemZero'),
  isNotAtStartOfBlock: not('isAtStartOfBlock'),
  isAtStartOfItem: reads('isAtPositionZero'),
  isSingleBlock: equalToProperty('startBlockIndex', 'endBlockIndex'),
  isSingleItem: equalToProperty('startItemIndex', 'endItemIndex'),
  hasSamePositionIndexes: equalToProperty('startPositionIndex', 'endPositionIndex'),
  isZeroWidth: and('isSingleBlockAndItem', 'hasSamePositionIndexes'),
  isSingleBlockAndItem: and('isSingleBlock', 'isSingleItem'),
  isSingleUneditableBlock: and('isSingleBlock', 'firstSelectedBlock.isNotEditable'),
  bomSelection: hash('startBlockIndex', 'startItemIndex', 'startPositionIndex', 'endBlockIndex', 'endItemIndex', 'endPositionIndex'),
  containsCodeBlock: Em.computed('selectedBlocks', function () {
    //TODO: tests
    let selectedBlocks = this.get('selectedBlocks');
    if (selectedBlocks) {
      return !!selectedBlocks.findBy('type', 'code');
    }
    return false;
  }),
  doesNotContainsCodeBlock: not('containsCodeBlock'),
  copy() {
    let data = this.getSelectionHash();
    return this.constructor.create().restoreCheckpoint(data);
  },
  firstSelectedPrimitiveBlock: Em.computed('firstSelectedBlock', 'startItemIndex', function () {
    if (this.get('firstSelectedBlock.isListType')) {
      return this.get('firstSelectedBlock.blockList').objectAt(this.get('startItemIndex'));
    } else {
      return this.get('firstSelectedBlock');
    }
  }),
  containsHeading: Em.computed('selectedBlocks', function () {
    return this._contains(['heading', 'subheading']);
  }),
  _contains(types) {
    types = Em.isArray(types) ? types : [types];
    let selectedBlocks = this.get('selectedBlocks');
    for (let i = 0; i < selectedBlocks.length; i++) {
      if (types.includes(selectedBlocks.objectAt(i).get('type'))) {
        return true;
      }
    }
    return false;
  },
  isAnchorActive: Em.computed('blockList.@each.html', 'info', function () {
    return this._isEntityActive('anchor');
  }),
  isBoldActive: Em.computed('blockList.@each.html', 'info', function () {
    return this._isEntityActive('bold');
  }),
  isItalicActive: Em.computed('blockList.@each.html', 'info', function () {
    return this._isEntityActive('italic');
  }),
  _isEntityActive(type) {
    let selectedBlocks = this.get('selectedBlocks');
    let block;
    let blockRange;
    for (let i = 0; i < selectedBlocks.length; i++) {
      block = selectedBlocks.objectAt(i);
      blockRange = this.get('blockList')._blockRangeForSelection(block, this);
      if (!block.hasEntityOfTypeCoveringRange(type, blockRange)) {
        return false;
      }
    }
    return true;
  },
  insertBlocks(blocks) {
    let blockListMutationResult = this.get('blockList').insertBlocksAtSelection(this, blocks);
    this.updateCaret(blockListMutationResult.get('newCaretIndex'), blockListMutationResult.get('newStartPoint'));
  },
  selectedBlocks: Em.computed('blockList.[]', 'startBlockIndex', 'endBlockIndex', function () {
    return this.get('blockList').slice(this.get('startBlockIndex'), this.get('endBlockIndex') + 1);
  }),
  caretIsAtEndOfEntityType(type) {
    // TODO Tests
    if (this.get('hasWidth')) {
      return;
    }
    let entities = this.get('selectedBlocks.firstObject.entities');
    return entities.getClosers(this.get('startPositionIndex')).any(function (entity) {
      return entity.get('type') === type;
    });
  },
  containsEntity(type) {
    return this.get('blockList').selectionContainsEntityType(this, type);
  },
  coveredByEntity(type) {
    return this.get('blockList').selectionCoveredByEntityType(this, type);
  },
  updateCaret(blockIndex, blockPoint) {
    this.setProperties({
      startBlockIndex: blockIndex,
      startItemIndex: blockPoint.get('itemIndex'),
      startPositionIndex: blockPoint.get('positionIndex'),
      endBlockIndex: blockIndex,
      endItemIndex: blockPoint.get('itemIndex'),
      endPositionIndex: blockPoint.get('positionIndex')
    });
  },
  info: computed('startBlockIndex', 'startItemIndex', 'startPositionIndex', 'endBlockIndex', 'endItemIndex', 'endPositionIndex', function () {
    return `${this.startBlockIndex}:${this.startItemIndex}:${this.startPositionIndex} -> ${this.endBlockIndex}:${this.endItemIndex}:${this.endPositionIndex}`;
  }),
  getSelectionHash() {
    return {
      startBlockIndex: this.get('startBlockIndex'),
      startItemIndex: this.get('startItemIndex'),
      startPositionIndex: this.get('startPositionIndex'),
      endBlockIndex: this.get('endBlockIndex'),
      endItemIndex: this.get('endItemIndex'),
      endPositionIndex: this.get('endPositionIndex')
    };
  },
  restoreCheckpoint(data) {
    let newSelection = this.constructor.create();
    newSelection.set('blockList', this.get('blockList'));
    newSelection.setProperties({
      startBlockIndex: data.startBlockIndex,
      startItemIndex: data.startItemIndex,
      startPositionIndex: data.startPositionIndex,
      endBlockIndex: data.endBlockIndex,
      endItemIndex: data.endItemIndex,
      endPositionIndex: data.endPositionIndex
    });
    this.setSelection(newSelection);
    return this;
  },
  setSelection(selection) {
    this._shiftMultiBlockSelectionOnBlockSeam(selection);
    this.setProperties({
      startBlockIndex: selection.get('startBlockIndex'),
      startItemIndex: selection.get('startItemIndex'),
      startPositionIndex: selection.get('startPositionIndex'),
      endBlockIndex: selection.get('endBlockIndex'),
      endItemIndex: selection.get('endItemIndex'),
      endPositionIndex: selection.get('endPositionIndex')
    });
  },
  _shiftMultiBlockSelectionOnBlockSeam(selection) {
    // Disallow selections of the form 0:0:0 -> 1:0:0 and convert them into 0:0:0 -> 0:0:{end of block}
    // We do this here because trying to coax DOM selection into doing this is more pain than it's worth
    if (!this.get('blockList') || selection.get('isSingleBlock')) {
      return;
    }
    let endBlock = this.get('blockList').objectAt(selection.get('endBlockIndex'));
    if (endBlock.get('isEditable') && selection.get('endPositionIndex') === 0 && selection.get('endItemIndex') === 0) {
      selection.decrementProperty('endBlockIndex');
      let newEndBlock = this.get('blockList').objectAt(selection.get('endBlockIndex'));
      if (newEndBlock.get('isListType')) {
        selection.set('endItemIndex', newEndBlock.get('blockList.length') - 1);
        selection.set('endPositionIndex', newEndBlock.get('blockList.lastObject.text.length'));
      } else {
        selection.set('endItemIndex', 0);
        selection.set('endPositionIndex', newEndBlock.get('text.length'));
      }
    }
  },
  hasPositionRange(startIndex, endIndex) {
    //TODO: GJ: tests
    return this.get('startPositionIndex') === startIndex && this.get('endPositionIndex') === endIndex;
  },
  assertValidRange(blockList) {
    let startBlock = blockList.objectAt(this.get('startBlockIndex'));
    let endBlock = blockList.objectAt(this.get('endBlockIndex'));
    if (Em.isEmpty(startBlock) || Em.isEmpty(endBlock)) {
      throw new Error(`selection block index out of bounds - ${this.get('info')}`);
    }
    let startPositionIsOutOfBounds;
    let startItemIsOutOfBounds;
    let endPositionIsOutOfBounds;
    let endItemIsOutOfBounds;
    if (startBlock.get('blockList')) {
      startItemIsOutOfBounds = this.get('startItemIndex') > startBlock.get('blockList.length');
      startPositionIsOutOfBounds = this.get('startPositionIndex') > startBlock.get('blockList').objectAt(this.get('startItemIndex')).get('text.length');
    } else {
      startItemIsOutOfBounds = this.get('startItemIndex') > 0;
      startPositionIsOutOfBounds = this.get('startPositionIndex') > startBlock.get('text.length');
    }
    if (endBlock.get('blockList')) {
      endItemIsOutOfBounds = this.get('endItemIndex') > endBlock.get('blockList.length');
      endPositionIsOutOfBounds = this.get('endPositionIndex') > endBlock.get('blockList').objectAt(this.get('endItemIndex')).get('text.length');
    } else {
      endItemIsOutOfBounds = this.get('endItemIndex') > 0;
      endPositionIsOutOfBounds = this.get('endPositionIndex') > endBlock.get('text.length');
    }
    if (startItemIsOutOfBounds || endItemIsOutOfBounds) {
      throw new Error(`selection item index out of bounds - ${this.get('info')}`);
    }
    if (startPositionIsOutOfBounds || endPositionIsOutOfBounds) {
      throw new Error(`selection position index out of bounds - ${this.get('info')}`);
    }
  },
  coveredBlocks: Em.computed('blockList', 'firstSelectedBlock', 'lastSelectedBlock', 'startPositionIndex', 'startItemIndex', 'endPositionIndex', 'endItemIndex', function () {
    // TODO: tests
    let coveredBlocks = [];
    let blockList = this.get('blockList');
    let startBlockIdx = this.get('startBlockIndex');
    let endBlockIdx = this.get('endBlockIndex');
    let firstSelectedBlock = this.get('firstSelectedBlock');
    let lastSelectedBlock = this.get('lastSelectedBlock');
    if (this._isCovered(firstSelectedBlock)) {
      coveredBlocks.pushObject(firstSelectedBlock);
    }
    if (endBlockIdx - startBlockIdx + 1 > 0) {
      coveredBlocks.pushObjects(blockList.slice(startBlockIdx + 1, endBlockIdx));
    }
    if (this._isCovered(lastSelectedBlock)) {
      coveredBlocks.pushObject(lastSelectedBlock);
    }
    return coveredBlocks;
  }),
  _isCovered(block) {
    if (!block) {
      return false; //TODO: figure out this could ever happen.
    }
    if (this.get('isNotAtItemOrPositionZero') && this.get('startBlockIndex') === this.get('blockList').indexOf(block)) {
      return false;
    }
    if (block.get('isListType')) {
      return this._isListTypeCovered(block);
    } else {
      return this._isNormalTypeCovered(block);
    }
  },
  _isNormalTypeCovered(block) {
    return this.get('endBlockIndex') > this.get('blockList').indexOf(block) || this.get('endPositionIndex') === block.get('text.length') || block.get('isNotEditable');
  },
  _isListTypeCovered(block) {
    return this.get('endBlockIndex') > this.get('blockList').indexOf(block) || this.get('endItemIndex') === block.get('blockList.length') - 1 && this.get('endPositionIndex') === block.get('blockList.lastObject.text.length');
  }
}).reopenClass({
  fromBlockRange(blockRange) {
    let blockSelection = this.create();
    blockSelection.setSelection(Em.Object.create({
      startBlockIndex: blockRange.get('startPoint.itemIndex'),
      startItemIndex: 0,
      startPositionIndex: blockRange.get('startPoint.positionIndex'),
      endBlockIndex: blockRange.get('endPoint.itemIndex'),
      endItemIndex: 0,
      endPositionIndex: blockRange.get('endPoint.positionIndex')
    }));
    return blockSelection;
  },
  createSelection(startBlockIndex, startItemIndex, startPositionIndex, endBlockIndex, endItemIndex, endPositionIndex) {
    let selection = this.create();
    selection.setProperties({
      startBlockIndex,
      startItemIndex,
      startPositionIndex,
      endBlockIndex,
      endItemIndex,
      endPositionIndex
    });
    return selection;
  }
});